// GDPR consent core + Google Consent Mode v2 gating.
// Necessary cookies are always on. Analytics defaults to DENIED and is only
// upgraded to GRANTED after the user explicitly accepts. "Decline" is given
// equal weight to "Accept". The stored choice can be changed at any time by
// re-opening the banner via the "Cookie settings" trigger.

export {};

type ConsentChoice = "granted" | "denied";

const STORAGE_KEY = "analytics-consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    openCookieSettings?: () => void;
  }
}

function gtagConsentUpdate(state: ConsentChoice): void {
  // Push directly to dataLayer so this works even if the gtag stub
  // has not finished initialising. Consent Mode reads from dataLayer.
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: state,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  } else {
    window.dataLayer.push([
      "consent",
      "update",
      {
        analytics_storage: state,
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      },
    ]);
  }
}

function getStoredConsent(): ConsentChoice | null {
  const value = window.localStorage.getItem(STORAGE_KEY);
  if (value === "granted" || value === "denied") {
    return value;
  }
  return null;
}

function storeConsent(state: ConsentChoice): void {
  window.localStorage.setItem(STORAGE_KEY, state);
}

window.addEventListener("DOMContentLoaded", () => {
  const banner = document.querySelector<HTMLDivElement>("#cookiebanner");
  if (!banner) {
    return;
  }

  const acceptButton = banner.querySelector<HTMLButtonElement>("#cookie-accept");
  const declineButton =
    banner.querySelector<HTMLButtonElement>("#cookie-decline");

  const showBanner = (): void => {
    banner.style.display = "flex";
    banner.removeAttribute("hidden");
    acceptButton?.focus();
  };

  const hideBanner = (): void => {
    banner.style.display = "none";
    banner.setAttribute("hidden", "");
  };

  const applyChoice = (state: ConsentChoice): void => {
    storeConsent(state);
    gtagConsentUpdate(state);
    hideBanner();
  };

  // Allow users to revisit their choice from a footer "Cookie settings" link.
  window.openCookieSettings = showBanner;

  acceptButton?.addEventListener("click", () => applyChoice("granted"));
  declineButton?.addEventListener("click", () => applyChoice("denied"));

  const stored = getStoredConsent();
  if (stored === null) {
    // No prior decision: analytics stays denied (set as default in <head>)
    // and the banner is shown so the user can make an informed choice.
    showBanner();
  } else {
    // Re-apply the saved decision on every load.
    gtagConsentUpdate(stored);
    hideBanner();
  }
});
